import { useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { History, ArrowLeft, DollarSign } from 'lucide-react';
import { api, fmt, type Row } from '../../lib/api';
import LoadingSpinner from '../../components/LoadingSpinner';
import ExcelExportButton from '../../components/ExcelExportButton';
import { useMe } from '../../lib/useMe';
import { cleanPolicyId } from '../../lib/format';
import type { SheetSpec } from '../../lib/xlsx-export';

export default function MyPayments() {
  const { agentId } = useMe();
  const navigate = useNavigate();

  const q = useQuery({
    queryKey: ['my-payments', agentId],
    queryFn: () => api.listPayments(undefined, agentId),
    enabled: !!agentId,
  });

  if (q.isLoading) return <LoadingSpinner label="Loading payments…" />;

  const payments = q.data?.payments ?? [];
  const total = payments.reduce((s, p) => s + (p.total || 0), 0);

  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-2xl font-semibold" style={{ color: 'var(--color-text)' }}>
            My Payments
          </h2>
          <p className="text-sm mt-1" style={{ color: 'var(--color-text-muted)' }}>
            Each row is one payment cycle. Click a payment to see exactly which policies are in it.
          </p>
        </div>
        {payments.length > 0 && (
          <ExcelExportButton
            filename={`my-payments.xlsx`}
            sheets={() => [
              {
                name: 'Payments',
                rows: [
                  ['Date', 'System', 'Carriers', 'Rows', 'Total'],
                  ...payments.map((p) => [
                    p.paid_at,
                    p.sys,
                    Object.entries(p.by_carrier || {})
                      .map(([c, v]) => `${c} (${v.rows})`)
                      .join(', '),
                    p.row_count,
                    p.total,
                  ]),
                ],
              },
            ]}
          />
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="metric-card metric-card-success">
          <div className="flex items-center gap-2 mb-1">
            <DollarSign size={14} style={{ color: 'var(--color-primary-text)' }} />
            <p
              className="text-xs uppercase tracking-wider"
              style={{ color: 'var(--color-text-muted)' }}
            >
              Lifetime paid
            </p>
          </div>
          <p className="text-2xl font-bold font-mono" style={{ color: 'var(--color-text)' }}>
            {fmt.money(total)}
          </p>
        </div>
        <div className="metric-card metric-card-primary">
          <div className="flex items-center gap-2 mb-1">
            <History size={14} style={{ color: 'var(--color-primary-text)' }} />
            <p
              className="text-xs uppercase tracking-wider"
              style={{ color: 'var(--color-text-muted)' }}
            >
              Payments
            </p>
          </div>
          <p className="text-2xl font-bold font-mono" style={{ color: 'var(--color-text)' }}>
            {payments.length}
          </p>
        </div>
      </div>

      {payments.length === 0 ? (
        <div className="card text-center py-12">
          <p style={{ color: 'var(--color-text-muted)' }}>
            No payments yet. Your first payment will appear here when one's created.
          </p>
        </div>
      ) : (
        <div className="card overflow-hidden p-0">
          <table className="table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Period</th>
                <th>Carriers</th>
                <th className="text-right">Rows</th>
                <th className="text-right">Amount</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {payments.map((p) => (
                <tr
                  key={p.payment_id}
                  onClick={() => navigate(`/my/payments/${p.payment_id}`)}
                  style={{ cursor: 'pointer' }}
                  title="Click to view payment detail"
                >
                  <td className="text-sm">{fmt.date(p.paid_at)}</td>
                  <td>
                    <span className="badge badge-primary">{p.period_label || p.sys}</span>
                  </td>
                  <td className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                    {Object.entries(p.by_carrier || {})
                      .map(([c, v]) => `${c} (${v.rows})`)
                      .join(', ')}
                  </td>
                  <td className="text-right font-mono">{p.row_count}</td>
                  <td
                    className="text-right font-mono font-semibold text-base"
                    style={{ color: (p.statement_net ?? p.total) < 0 ? '#ef4444' : undefined }}
                  >
                    {fmt.money(p.statement_net ?? p.total)}
                    {p.held && (
                      <span className="badge badge-warning text-xs ml-2" title="Held — $0 paid; balance carried forward to your next payment.">
                        held
                      </span>
                    )}
                  </td>
                  <td className="text-right text-xs" style={{ color: 'var(--color-primary)' }}>
                    View →
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function MyPaymentDetail() {
  const { paymentId } = useParams<{ paymentId: string }>();
  const [activeCarrier, setActiveCarrier] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ['my-payment', paymentId],
    queryFn: () => api.getPayment(paymentId!),
    enabled: !!paymentId,
  });

  if (q.isLoading) return <LoadingSpinner label="Loading payment…" />;
  if (q.isError) return <div className="alert alert-error"><p>{(q.error as Error).message}</p></div>;

  const data = q.data!;

  // Group rows by carrier — each carrier becomes a tab + a worksheet.
  const byCarrier = new Map<string, { rows: Row[]; net: number }>();
  for (const r of data.rows) {
    const c = r.carrier || '?';
    if (!byCarrier.has(c)) byCarrier.set(c, { rows: [], net: 0 });
    const b = byCarrier.get(c)!;
    b.rows.push(r);
    b.net += r.net_amount || 0;
  }
  const carrierList = [...byCarrier.keys()].sort();
  const currentCarrier =
    activeCarrier && byCarrier.has(activeCarrier) ? activeCarrier : carrierList[0] ?? null;
  const currentRows = currentCarrier ? byCarrier.get(currentCarrier)!.rows : [];
  const currentNet = currentCarrier ? byCarrier.get(currentCarrier)!.net : 0;

  const buildSheets = (): SheetSpec[] => {
    // Agents see only the commission they were paid — raw/override are
    // an agency-side concern they don't need.
    const headers = ['Policy', 'Policyholder', 'Commission', 'Paid-to date'];
    return carrierList.map((carrier) => {
      const bucket = byCarrier.get(carrier)!;
      const rows: (string | number | null)[][] = [headers];
      for (const r of bucket.rows.slice().sort((a, b) => (b.net_amount || 0) - (a.net_amount || 0))) {
        rows.push([
          cleanPolicyId(r.policy_id, r.writing_agent_id),
          r.policyholder_name ?? '',
          r.net_amount ?? null,
          r.paid_to_date ?? r.transaction_date ?? '',
        ]);
      }
      rows.push([
        `Total · ${bucket.rows.length} rows`,
        '',
        bucket.rows.reduce((s, r) => s + (r.net_amount || 0), 0),
        '',
      ]);
      return { name: carrier, rows };
    });
  };
  const exportFilename = `payment-${fmt.dateOnly(data.payment.paid_at).replace(/\//g, '-')}-${data.payment.payment_id}.xlsx`;

  return (
    <div className="space-y-4 animate-fadeIn">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Link to="/my/payments" className="btn-icon" style={{ color: 'var(--color-text-muted)' }}>
            <ArrowLeft size={20} />
          </Link>
          <h2 className="text-2xl font-semibold" style={{ color: 'var(--color-text)' }}>
            Payment · {fmt.dateOnly(data.payment.paid_at)}
          </h2>
        </div>
        {carrierList.length > 0 && (
          <ExcelExportButton filename={exportFilename} sheets={buildSheets} />
        )}
      </div>

      {/* Carry-forward breakdown — only when a balance was carried in or this
          cycle was held. Earnings + carried balance = what paid (or held). */}
      {((data.payment.carried_in ?? 0) < 0 || data.payment.held) && (
        <div className="card p-4">
          <div className="flex flex-wrap gap-x-10 gap-y-2 items-end">
            <div>
              <p className="text-xs uppercase tracking-wider" style={{ color: 'var(--color-text-muted)' }}>
                Earnings this cycle
              </p>
              <p className="font-mono font-semibold" style={{ color: 'var(--color-text)' }}>
                {fmt.money(data.rows.reduce((s, r) => s + (r.net_amount || 0), 0))}
              </p>
            </div>
            {(data.payment.carried_in ?? 0) < 0 && (
              <div>
                <p className="text-xs uppercase tracking-wider" style={{ color: 'var(--color-text-muted)' }}>
                  Carryforward balance applied
                </p>
                <p className="font-mono font-semibold" style={{ color: '#ef4444' }}>
                  {fmt.money(data.payment.carried_in ?? 0)}
                </p>
              </div>
            )}
            <div>
              <p className="text-xs uppercase tracking-wider" style={{ color: 'var(--color-text-muted)' }}>
                {data.payment.held ? 'Held — carried forward' : 'Net paid'}
              </p>
              <p
                className="font-mono font-semibold text-lg"
                style={{ color: data.payment.held ? '#ef4444' : 'var(--color-text)' }}
              >
                {fmt.money(data.payment.held ? data.payment.statement_net ?? 0 : data.payment.total)}
              </p>
            </div>
          </div>
          {data.payment.held && (
            <p className="text-xs mt-2" style={{ color: 'var(--color-text-muted)' }}>
              This statement wasn't paid out — the balance carries forward to your next payment.
            </p>
          )}
        </div>
      )}

      {/* Cards = clickable carrier tabs (mirrors Policy Data) */}
      {carrierList.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
          {carrierList.map((c) => {
            const b = byCarrier.get(c)!;
            return (
              <button
                key={c}
                onClick={() => setActiveCarrier(c)}
                className="metric-card metric-card-primary text-left"
                style={{
                  borderColor: currentCarrier === c ? 'var(--color-primary)' : undefined,
                  borderWidth: currentCarrier === c ? 2 : undefined,
                }}
              >
                <p
                  className="text-xs uppercase tracking-wider mb-1"
                  style={{ color: 'var(--color-text-muted)' }}
                >
                  {c}
                </p>
                <p className="text-xl font-bold font-mono" style={{ color: 'var(--color-text)' }}>
                  {fmt.money(b.net)}
                </p>
                <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>
                  {b.rows.length} rows
                </p>
              </button>
            );
          })}
        </div>
      )}

      {currentCarrier && (
        <div className="card overflow-x-auto p-0">
          <div className="px-4 py-3" style={{ borderBottom: '1px solid var(--color-border)' }}>
            <h3 className="text-sm font-semibold" style={{ color: 'var(--color-text)' }}>
              {currentCarrier} · {fmt.money(currentNet)} ({currentRows.length} rows)
            </h3>
          </div>
          <table className="table" style={{ tableLayout: 'fixed' }}>
            <thead>
              <tr>
                <th style={{ width: '28%' }}>Policy</th>
                <th style={{ width: '34%' }}>Policyholder</th>
                <th className="text-right" style={{ width: '18%' }}>Commission</th>
                <th style={{ width: '20%' }}>Paid-to date</th>
              </tr>
            </thead>
            <tbody>
              {currentRows
                .slice()
                .sort((a, b) => (b.net_amount || 0) - (a.net_amount || 0))
                .map((r) => (
                  <tr key={`${r.batch_id}-${r.row_idx}`}>
                    <td className="font-mono text-xs">
                      {cleanPolicyId(r.policy_id, r.writing_agent_id)}
                    </td>
                    <td>{r.policyholder_name}</td>
                    <td className="text-right font-mono font-semibold">
                      {fmt.money(r.net_amount)}
                    </td>
                    <td className="text-xs">
                      {fmt.dateOnly(r.paid_to_date ?? r.transaction_date)}
                    </td>
                  </tr>
                ))}
            </tbody>
            <tfoot>
              <tr style={{ backgroundColor: 'var(--color-table-header)', fontWeight: 600 }}>
                <td colSpan={2}>Total · {currentRows.length} rows</td>
                <td className="text-right font-mono">
                  {fmt.money(currentRows.reduce((s, r) => s + (r.net_amount || 0), 0))}
                </td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}
